'use client';

import type { PropsWithChildren } from 'react';
import { createContext, useMemo } from 'react';

import type { Inventory, ProductVariantInventory } from '@libs/algolia';
import { DeliveryType, LOCALIZED, type Nullable } from '@libs/core';

import { useProductDetails } from '@app/pdp/hooks';
import { getSelectedInventory } from '@app/pdp/utils';
import { useProductsInventories } from '@app/pdp/components/AddToCart/hooks/useProductsInventories';

type ProductsInventoryContextProps = {
    inventories?: ProductVariantInventory;
    selectedProductInventories: {
        bopis: Inventory;
        sdd: Inventory;
        web: Inventory;
    };
    selectedDeliveryMethodInventory: Inventory;
    inventoryLoading: boolean;
};
type ProductsInventoryProviderProps = {
    defaultValues: ProductVariantInventory;
};
const inventoryIds = [
    DeliveryType.BOPIS,
    DeliveryType.SDD,
    LOCALIZED.webInventory,
];

export const ProductsInventoryContext =
    createContext<Nullable<ProductsInventoryContextProps>>(undefined);

export default function ProductsInventoryProvider({
    children,
    defaultValues,
}: PropsWithChildren<ProductsInventoryProviderProps>) {
    const { selectedProduct, selectedDeliveryMethod } = useProductDetails();
    const { data: inventories, isLoading: inventoryLoading } =
        useProductsInventories({ defaultValues });

    const [bopis, sdd, web] = inventoryIds.map(deliveryType =>
        getSelectedInventory({
            inventories,
            productId: selectedProduct.objectID,
            deliveryType,
        })
    );

    const selectedProductInventories = useMemo(
        () => ({
            bopis,
            sdd,
            web,
        }),
        [bopis, sdd, web]
    );

    const selectedDeliveryMethodInventory = useMemo(
        () =>
            selectedProductInventories[
                selectedDeliveryMethod as keyof typeof selectedProductInventories
            ] ?? web,
        [selectedDeliveryMethod, selectedProductInventories, web]
    );

    const values: ProductsInventoryContextProps = useMemo(
        () => ({
            inventories,
            selectedProductInventories,
            selectedDeliveryMethodInventory,
            inventoryLoading,
        }),
        [
            inventories,
            selectedDeliveryMethodInventory,
            selectedProductInventories,
            inventoryLoading,
        ]
    );

    return (
        <ProductsInventoryContext.Provider value={values}>
            {children}
        </ProductsInventoryContext.Provider>
    );
}
